import React from 'react'
import * as FlexIcons from 'core-system/Icons/Flex'
import palette from 'core-system/Themes/palette'
import ProgramEditingSubSection from 'features/Programs/Editing/Components/ProgramEditingSubSection'
import FormattingUtils from 'shared/FormattingUtils'
import styled from 'styled-components'
import moment from 'moment'
import TransitParkingToggle from 'features/Programs/Editing/Components/TransitParkingToggle'
import TransitParkingEmployerContribution from 'features/Programs/Editing/Components/TransitParkingEmployerContribution'
import FinalizeAllocationsDate from 'features/Programs/Editing/Components/FinalizeAllocationsDate'
import ProgramSectionSubHeading from 'features/Programs/Editing/Components/ProgramSectionSubHeading'
import PayrollSchedule from 'features/Programs/Editing/Components/PayrollSchedule'
import EmployerMaxAllocationLimit from 'features/Programs/Editing/Components/EmployerMaxAllocationLimit'
import { Month } from 'shared/DateUtils'
import {
  qtfType,
  qtfBenefitsType,
  qtfPayrollScheduleType,
} from '../Shared/types'
import { QTF_INITIAL_STATE } from './ProgramEditingSlice'

const StyledLink = styled.a`
  color: ${palette.secondary.purple700};
  text-decoration: underline;

  &:hover {
    color: ${palette.primary.pink700};
  }
`

interface QtfProgramEditingProps {
  qtf: qtfType
  irsTaxLimit: number
  selectedDate: Month
  changeQtfBenefits: (type: qtfBenefitsType, value: boolean) => void
  changeQtfEmployerContributions: (type: qtfBenefitsType, value: number) => void
  changeQtfFinalizeAllocation: (day: number) => void
  changeQtfPayrollSchedule: (payrollSchedule: qtfPayrollScheduleType) => void
  changeQtfMaxAllocationLimit: (type: qtfBenefitsType, limit: number) => void
  canEditAllocationsDate: boolean
}

const QtfProgramEditing = ({
  qtf = QTF_INITIAL_STATE,
  irsTaxLimit = 0,
  selectedDate,
  changeQtfBenefits,
  changeQtfEmployerContributions,
  changeQtfFinalizeAllocation,
  changeQtfPayrollSchedule,
  changeQtfMaxAllocationLimit,
  canEditAllocationsDate,
}: QtfProgramEditingProps) => {
  const currentYear = moment().year()

  const onContributionDayChange = (day: number) => {
    // We do not allows to beyond 27th
    if (day > 27) {
      return
    }
    changeQtfFinalizeAllocation(day)
  }

  return (
    <>
      <ProgramEditingSubSection>
        <ProgramSectionSubHeading title='Payroll Schedule' />
        <PayrollSchedule
          payrollSchedule={qtf.payrollSchedule}
          onPayrollScheduleChange={changeQtfPayrollSchedule}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramSectionSubHeading title='Which benefits would you like to offer?' />
        <TransitParkingToggle
          type='Transit'
          icon={
            <FlexIcons.Transit width={32} height={32} color={palette.white} />
          }
          canEdit={true}
          active={qtf.benefits.transit}
          handleToggle={() =>
            changeQtfBenefits('transit', !qtf.benefits.transit)
          }
        />
        <TransitParkingToggle
          type='Parking'
          icon={
            <FlexIcons.Parking width={32} height={32} color={palette.white} />
          }
          canEdit={true}
          active={qtf.benefits.parking}
          handleToggle={() =>
            changeQtfBenefits('parking', !qtf.benefits.parking)
          }
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramSectionSubHeading
          title='How much will you, as an employer, contribute to each program?'
          subtitle={
            <>
              <StyledLink
                href={`https://www.irs.gov/newsroom/irs-provides-tax-inflation-adjustments-for-tax-year-${currentYear}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                According to the IRS
              </StyledLink>
              , as an employer, you may contribute up to{' '}
              {FormattingUtils.formatDollar(irsTaxLimit, 0) + ' '}
              for Parking and up to{' '}
              {FormattingUtils.formatDollar(irsTaxLimit, 0)} for Transit per
              month.
            </>
          }
        />
        <TransitParkingEmployerContribution
          type='Transit'
          icon={
            <FlexIcons.Transit width={32} height={32} color={palette.white} />
          }
          canEdit={true}
          contributionAmount={qtf.employerContributions.transit}
          onContributionChange={(amount) =>
            changeQtfEmployerContributions('transit', amount * 100)
          }
          maxContribution={irsTaxLimit}
          active={qtf.benefits.transit}
        />
        <TransitParkingEmployerContribution
          type='Parking'
          icon={
            <FlexIcons.Parking width={32} height={32} color={palette.white} />
          }
          canEdit={true}
          contributionAmount={qtf.employerContributions.parking}
          onContributionChange={(amount) =>
            changeQtfEmployerContributions('parking', amount * 100)
          }
          maxContribution={irsTaxLimit}
          active={qtf.benefits.parking}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramSectionSubHeading
          title='Employer Max Allocation Limit for Pre-Tax Benefits'
          subtitle='Allows employers to cap employees monthly pre-tax allocations below the IRS limit for each commuter program.'
        />
        <EmployerMaxAllocationLimit
          type='Transit'
          icon={
            <FlexIcons.Transit width={32} height={32} color={palette.white} />
          }
          maxAllocationLimit={qtf.maxAllocationLimit.transit}
          onMaxAllocationChange={(amount) =>
            changeQtfMaxAllocationLimit('transit', amount * 100)
          }
          irsTaxLimit={irsTaxLimit}
          active={qtf.benefits.transit}
          employerContribution={qtf.employerContributions.transit}
        />
        <EmployerMaxAllocationLimit
          type='Parking'
          icon={
            <FlexIcons.Parking width={32} height={32} color={palette.white} />
          }
          maxAllocationLimit={qtf.maxAllocationLimit.parking}
          onMaxAllocationChange={(amount) =>
            changeQtfMaxAllocationLimit('parking', amount * 100)
          }
          irsTaxLimit={irsTaxLimit}
          active={qtf.benefits.parking}
          employerContribution={qtf.employerContributions.parking}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection isLastSection>
        <FinalizeAllocationsDate
          setupMode='manual'
          canEdit={canEditAllocationsDate}
          onContributionDayChange={onContributionDayChange}
          contributionDays={qtf.finalizeAllocation}
          contribStartDate={moment(selectedDate.iso)}
        />
      </ProgramEditingSubSection>
    </>
  )
}

export default React.memo(QtfProgramEditing)
